import type { MeResponse } from "@seagames/contracts";
import type { QueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import { reportOutcome } from "../api/connection";
import { meQueryOptions } from "./session";

/**
 * The `_app` guard's session check, built so a reload on bad venue Wi-Fi is
 * never a dead white screen:
 *
 *   - ONE attempt, no query-level retry (that was 2 + 4 + 8 s of nothing).
 *     The "Can't reach the server" screen owns the retrying instead, with
 *     the screen up.
 *   - A request that hangs (black-holed Wi-Fi: no refusal, no answer) counts
 *     as no answer after SESSION_CHECK_TIMEOUT_MS. The fetch itself keeps
 *     going, and the next attempt joins it (react-query dedupes in-flight
 *     queries), so a slow server that does answer is still taken.
 *
 * No answer is NOT a verdict: the tokens stay, nobody is signed out.
 */
export const SESSION_CHECK_TIMEOUT_MS = 4_000;

/** Backoff for the background retries: 2, 4, 8, 15, then every 30 s. */
const RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 15_000, 30_000] as const;

export function sessionRetryDelay(attempt: number): number {
	return RETRY_DELAYS_MS[Math.min(Math.max(attempt, 0), RETRY_DELAYS_MS.length - 1)]!;
}

/** Thrown by the `_app` guard when the server did not answer; the root error boundary renders the retry screen. */
export class SessionUnreachableError extends Error {
	override readonly cause: ApiError;
	constructor(cause: ApiError) {
		super(cause.message);
		this.name = "SessionUnreachableError";
		this.cause = cause;
	}
}

export const isSessionUnreachable = (e: unknown): e is SessionUnreachableError => e instanceof SessionUnreachableError;

/** Cached `/auth/me` if we have it; otherwise one bounded attempt. Rejects with `ApiError`. */
export function checkSession(queryClient: QueryClient): Promise<MeResponse> {
	const cached = queryClient.getQueryData(meQueryOptions.queryKey);
	if (cached) return Promise.resolve(cached);

	const fetching = queryClient.fetchQuery({ ...meQueryOptions, retry: false });
	let timer: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<never>((_, reject) => {
		timer = setTimeout(() => {
			reportOutcome("offline");
			reject(new ApiError("NETWORK", 0, "The server did not answer in time."));
		}, SESSION_CHECK_TIMEOUT_MS);
	});
	return Promise.race([fetching, timeout]).finally(() => clearTimeout(timer));
}
